/**
 * Segment-click adapter for the Status Overview. Delegates the
 * attribute-agnostic single-select toggle to
 * `toggleSingleSelectFilterToken`; the validation-specific work here
 * is limited to building the correct chip shape per bar and scoping
 * the "select group" to chips belonging to the same bar.
 *
 * Two chip shapes coexist:
 *
 * - `validationStatus` compound chips (e.g. `LVS_PASSED`) — used by
 *   per-pipeline bars. Prefix identifies the bar.
 *
 * - Direct chips (e.g. `validationOverallStatus = PASSED`) — used by
 *   bars whose backing attribute stores the scalar status directly.
 *
 * Single-select is per-bar: clicking on the LVS bar only replaces
 * `LVS_*` chips and leaves any other bar's chip alone. This matches
 * the highlight behavior, where each bar tracks its own
 * `activeStatusValues` independently.
 */

import type {
  PropertyFilter,
  PropertyFilterGroup,
} from "../../searchSideBar/searchSideBarConnector";
import { toggleSingleSelectFilterToken } from "../../searchSideBar/singleSelectFilterToken";
import {
  filterChipForSegment,
  isBarChipPropertyKey,
  STATUS_ATTRIBUTE_SPECS,
  StatusSegment,
  VALIDATION_STATUS_ATTRIBUTE_KEY,
} from "./chartData";

/**
 * Returns a fresh `PropertyFilterGroup` with the clicked bar's status
 * chip added, replaced, or toggled off. `current` is never mutated.
 * Unknown `segment.attributeKey` values are a no-op.
 */
export function toggleStatusFilterToken(
  current: PropertyFilterGroup,
  segment: Pick<StatusSegment, "attributeKey" | "statusValue">
): PropertyFilterGroup {
  const spec = STATUS_ATTRIBUTE_SPECS.find(
    (s) => s.attributeKey === segment.attributeKey
  );
  if (spec === undefined) {
    return current;
  }
  const chip = filterChipForSegment(spec, segment.statusValue);
  if (chip === undefined) {
    return current;
  }

  const newFilter: PropertyFilter = {
    type: "PropertyFilter",
    operator: "filter",
    propertyKey: chip.propertyKey,
    value: chip.value,
  };

  // Strip only chips belonging to this bar so cross-bar single-select
  // stays independent: an LVS click doesn't wipe a HATTRICK chip.
  const isMemberOfThisBar = (f: PropertyFilter): boolean => {
    if (spec.filterChip.kind === "validationStatus") {
      return (
        f.propertyKey === VALIDATION_STATUS_ATTRIBUTE_KEY &&
        typeof f.value === "string" &&
        f.value.startsWith(`${spec.filterChip.prefix}_`)
      );
    }
    return f.propertyKey === spec.attributeKey;
  };

  return toggleSingleSelectFilterToken(current, newFilter, isMemberOfThisBar);
}

/**
 * Returns a fresh `PropertyFilterGroup` with every chip that could
 * originate from any Status Overview bar removed from the nested
 * attribute group. `current` is never mutated. Used to compute the
 * baseline query for the aggregation, so chart-driven drill-down
 * chips don't reshape the bars that produced them.
 */
export function stripValidationStatusFilters(
  current: PropertyFilterGroup
): PropertyFilterGroup {
  const asinFilter = current.filters.find(
    (f): f is PropertyFilter =>
      f.type === "PropertyFilter" && f.propertyKey === "asin"
  );
  const attributeGroup = current.filters.find(
    (f): f is PropertyFilterGroup => f.type === "PropertyFilterGroup"
  );

  const strippedAttributeFilters = (attributeGroup?.filters ?? []).filter(
    (f) => !(f.type === "PropertyFilter" && isBarChipPropertyKey(f.propertyKey))
  );

  const rootFilters: Array<PropertyFilter | PropertyFilterGroup> = [];
  if (asinFilter !== undefined) {
    rootFilters.push(asinFilter);
  }
  if (strippedAttributeFilters.length > 0) {
    rootFilters.push({
      type: "PropertyFilterGroup",
      operation: attributeGroup?.operation ?? "and",
      filters: strippedAttributeFilters,
    });
  }

  return {
    type: "PropertyFilterGroup",
    operation: current.operation,
    filters: rootFilters,
  };
}
